import React from "react";
import {
  Col,
  Row,
  Input,
  Label,
  FormFeedback,
} from "reactstrap";
import Select from "react-select";

interface RoleTaskFormProps {
  validation: any;
  roles?: any[];
  tasks?: any[];
  roleTasks?: any[];
  selectedRoleId?: number | null;
  selectedTaskId?: number | null;
}

const RoleTaskForm: React.FC<RoleTaskFormProps> = ({
  validation,
  roles = [],
  tasks = [],
  roleTasks = [],
  selectedRoleId = null,
  selectedTaskId = null,
}) => {
  // Filter to show only active roles
  const activeRoles = React.useMemo(() => {
    if (!Array.isArray(roles)) return [];
    return roles.filter((role: any) => 
      role.status?.toLowerCase() === "active"
    );
  }, [roles]);

  const roleOptions = activeRoles.map((role) => ({
    label: role.rolename,
    value: role.roleid,
  }));

  // Filter to show only active tasks
  const activeTasks = React.useMemo(() => {
    if (!Array.isArray(tasks)) return [];
    return tasks.filter((task: any) => 
      task.status?.toLowerCase() === "active"
    );
  }, [tasks]);

  // Get the selected role ID from form or prop
  const currentRoleId = React.useMemo(() => {
    return Number(validation.values.roleid || selectedRoleId) || null;
  }, [validation.values.roleid, selectedRoleId]);

  // Filter out tasks that already have a combination with the selected role
  const availableTasks = React.useMemo(() => {
    if (!currentRoleId) return activeTasks;
    
    // Get task IDs that already have a combination with the selected role
    const existingTaskIds = roleTasks
      .filter((roleTask: any) => Number(roleTask.roleid) === Number(currentRoleId))
      .map((roleTask: any) => Number(roleTask.taskid));
    
    // Filter out tasks that already exist for this role
    // But keep the currently selected task (if editing)
    return activeTasks.filter((task: any) => {
      const taskId = Number(task.taskid);
      const isCurrentlySelected = taskId === Number(validation.values.taskid || selectedTaskId);
      return !existingTaskIds.includes(taskId) || isCurrentlySelected;
    });
  }, [activeTasks, currentRoleId, roleTasks, validation.values.taskid, selectedTaskId]);

  const taskOptions = availableTasks.map((task) => ({
    label: task.taskname,
    value: task.taskid,
  }));

  return (
    <Row className="g-3">
      <Col md={12}>
        <Label className="fw-bold mb-1">
          Role <span className="text-danger">*</span>
        </Label>
        <div style={{ width: "100%" }}>
          <Select
            options={roleOptions}
            name="roleid"
            placeholder="Select Role"
            value={roleOptions.find((opt) => {
              const formValue = validation.values.roleid;
              const selectedValue = selectedRoleId;
              return opt.value === Number(formValue || selectedValue);
            }) || null}
            onChange={(e: { value: any }) => {
              validation.setFieldValue("roleid", e?.value);
              // Clear taskid when role changes to prevent invalid combinations
              validation.setFieldValue("taskid", "");
              validation.setFieldTouched("taskid", false);
            }}
            onBlur={() => validation.setFieldTouched("roleid", true)}
            classNamePrefix="react-select"
            className={validation.touched.roleid && validation.errors.roleid ? "react-select-error" : ""}
            isClearable
          />
        </div>
        {validation.touched.roleid && validation.errors.roleid && (
          <div className="invalid-feedback d-block">{validation.errors.roleid}</div>
        )}
      </Col>

      <Col md={12}>
        <Label className="fw-bold mb-1">
          Task <span className="text-danger">*</span>
        </Label>
        <div style={{ width: "100%" }}>
          <Select
            options={taskOptions}
            name="taskid"
            placeholder="Select Task"
            value={taskOptions.find((opt) => {
              const formValue = validation.values.taskid;
              const selectedValue = selectedTaskId;
              return opt.value === Number(formValue || selectedValue);
            }) || null}
            onChange={(e: { value: any }) => {
              validation.setFieldValue("taskid", e?.value);
              // Clear roleid error when task changes to allow re-validation
              if (validation.errors.roleid && validation.touched.roleid) {
                validation.setFieldTouched("roleid", false);
              }
            }}
            onBlur={() => validation.setFieldTouched("taskid", true)}
            classNamePrefix="react-select"
            className={validation.touched.taskid && validation.errors.taskid ? "react-select-error" : ""}
            isClearable
          />
        </div>
        {validation.touched.taskid && validation.errors.taskid && (
          <div className="invalid-feedback d-block">{validation.errors.taskid}</div>
        )}
      </Col>
    </Row>
  );
};

export default RoleTaskForm;
